import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import { AxiosResponse } from 'axios';

// Types
import { User } from 'Shared/constants/types';
import { BaInfoStatus, BaPersonInfo, InvalidAssessmentId } from 'Shared/types/BaInfo.types';

// Constants
import { getConversationTip } from 'Shared/constants/ConversationTips.constants';

// Actions
import * as PiIntegrationsActions from 'Shared/actions/PiIntegrations.actions';

// Selectors
import { getBaInfoByUserId, getPendingAssessmentIdByUserSlug } from 'Shared/selectors/BaInfo.selectors';

// Hooks
import { useOrganization } from './use-organization';
import { useToggle } from 'Shared/utils/hooks';

// Utils
import { getBADemoData } from 'Shared/utils/piDemoData.utils';

type Props = {
  user: User;
  withFetch?: boolean;
};

export const useBaProfile = ({ user, withFetch = true }: Props) => {
  const dispatch = useDispatch();

  const [status, setStatus] = useState<BaInfoStatus>();
  const [isFetching, toggleIsFetching] = useToggle(false);
  const { organization, isPiDemoAccount } = useOrganization();
  const baProfileFromStore: BaPersonInfo = useSelector(state =>
    getBaInfoByUserId(state, {
      userGuid: user?.piUserGuid,
    })
  );
  const baProfile: BaPersonInfo = isPiDemoAccount && user ? getBADemoData(user.firstName) : baProfileFromStore;

  const pendingAssesmentId = useSelector(state =>
    getPendingAssessmentIdByUserSlug(state, { userGuid: user?.piUserGuid })
  );

  const hasPendingBa = baProfile?.hasPendingBa && !baProfile?.referencePattern?.referencePatternName;
  const referencePatternName = baProfile?.referencePattern?.referencePatternName;
  const strengths: Array<string> = baProfile?.strengths?.split('|');
  const cautionAreas: Array<string> = baProfile?.cautionAreas?.split('|');
  const howToInteractWith: Array<string> = baProfile?.personNeedsListContent?.map(need => need.needsListContentTitle);
  const preferredWorkStylesSummary: Array<string> = baProfile?.preferredWorkStyles?.map(
    // the preferredWorkStyles key is an array of string with each string containing multiple
    // html <li> inside. we strip out the html and take just the first sentence for the summary
    // also remove the [fname] placeholder  - same logic from pi-client-inspire https://github.com/predictiveindex/pi-client-inspire/blob/b5fdb75cae53dbfcca789117ceb77282de632801/inspire/client/src/app/services/person/person-work-style.service.ts#L47
    ws =>
      ws
        .replace(/(<([^>]+)>)/gi, '')
        .replace('[fname]', '')
        .split('.')[0]
  );

  const conversationTips: Array<string> =
    baProfile?.factors?.map(factor =>
      getConversationTip({ factor: factor.letter, sigmaZone: factor.sigmaZone }).replace('%name%', user.firstName)
    ) || []; // catch any null returned from helper (should never happen)

  const hasPendingAssesmentId = Boolean(pendingAssesmentId);

  useEffect(() => {
    if (isPiDemoAccount) {
      setStatus(BaInfoStatus.BA_FOUND);
      return;
    }

    if (!withFetch) return;
    if (baProfile && baProfile.assessmentId !== InvalidAssessmentId) return;
    if (!user) return;
    if (!user?.piUserGuid) return;
    toggleIsFetching();
    setStatus(BaInfoStatus.BA_FETCHING);
    dispatch(
      PiIntegrationsActions.getUser({
        companyGuid: organization.pi_company_guid,
        userGuid: user?.piUserGuid,
      })
    ).then((response: AxiosResponse) => {
      setStatus(
        response.status === 200 && response.data?.assessmentId !== InvalidAssessmentId
          ? BaInfoStatus.BA_FOUND
          : BaInfoStatus.BA_NOT_FOUND
      );
      toggleIsFetching();
    });
  }, []);

  return {
    status,
    isFetching,
    hasPendingBa,
    hasPendingAssesmentId,
    baProfile,
    referencePatternName,
    strengths,
    cautionAreas,
    howToInteractWith,
    preferredWorkStylesSummary,
    conversationTips,
  };
};
